import FormControl from '@material-ui/core/FormControl';
import MuiFormLabel, { FormLabelProps } from '@material-ui/core/FormLabel';
import { fade, withStyles } from '@material-ui/core/styles';
import React, { useCallback } from 'react';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import {
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography as ExtendTypography
} from '../../../../ui';
import {
  AppleIcon,
  BancontactIcon,
  CashPaymentIcon,
  CreditCardIcon,
  GoogleMicrosoftIcon,
  IdealIcon,
  PayPalIcon
} from '../../../../ui/Icon/AppIcons';
import { basketActions } from '../../../redux';
import { paymentSelectors } from '../../../selectors';

const Typography = ExtendTypography as any;

const FdRadio = withStyles(
  {
    root: {
      // &&& to override in Radio>IconButton>ButtonBase
      '&&&:hover': {
        backgroundColor: 'transparent'
      },
      // fix Safari issue checked dot out of center
      '& svg:first-child': { transform: 'scale(1)' },
      '& svg': {
        fontSize: '16px'
      }
    }
  },
  { name: 'FdRadio' }
)(Radio);

const FdFormControlLabel = withStyles(
  (theme) => ({
    root: {
      margin: 0,
      width: '100%',
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      // paddingLeft: theme.spacing(0.5),
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: theme.palette.divider,
      '&:hover': {
        backgroundColor: fade(theme.palette.primary.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      },
      '& .svg-icon': {
        display: 'flex',
        minWidth: '36px'
      },
      '& .MuiFormControlLabel-label.Mui-disabled': {
        '& .MuiTypography-root': {
          color: theme.palette.action.disabled
        }
      }
    },
    label: {
      fontSize: 15,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  }),
  { name: 'FdFormControlLabel' }
)(FormControlLabel);

const FormLabel = withStyles<'root', {}, FormLabelProps & { component: any }>(
  {
    root: {
      // override for #flipdish-menu legend
      borderBottom: 'none',
      marginBottom: '6px'
    }
  },
  { name: 'FdFormLabel' }
)(MuiFormLabel);

export type ParsePaymentProps = {
  payment: WebOrder.State.Payment;
  translate: ReturnType<typeof getTranslate>;
};
export function parsePayment({ payment, translate }: ParsePaymentProps) {
  let text: React.ReactNode = payment.Description;
  let icon: React.ReactNode = null;
  let fdKey: string = `${payment.PaymentAccountType}-option`;

  switch (payment.PaymentAccountType) {
    case 'Bancontact': {
      text = payment.Description;
      icon = <BancontactIcon height={27} width={27} aria-hidden />;
      break;
    }

    case 'BrainTreePayPalPaymentAccount': {
      icon = <PayPalIcon height={27} aria-hidden />;

      if (payment.paymentId === 'paypal') {
        text = translate('New_paypal_account') as string;
      } else {
        text = (
          <>
            {'PayPal '}
            <Typography noWrap color="textSecondary" component="span">
              ({payment.Description.slice(payment.Description.indexOf(' '))})
            </Typography>
          </>
        );
      }
      break;
    }

    case 'CardPaymentAccount': {
      icon = <CreditCardIcon color="action" fontSize="default" />;

      if (payment.paymentId === 'new_card') {
        text = translate('New_card') as string;
        fdKey = 'add-card-option';
      } else {
        const cardNumber = payment.Description.slice(payment.Description.indexOf('*'));
        text = `${translate('Pay_with')} ${translate('Card')} ${cardNumber || ''}`;
      }
      break;
    }

    case 'CashPaymentAccount': {
      text = translate('Cash_wo') as string;
      icon = (
        <span style={{ color: '#67a072' }}>
          <CashPaymentIcon color="inherit" />
        </span>
      );
      break;
    }

    case 'Ideal': {
      text = payment.Description;
      icon = <IdealIcon height={27} aria-hidden />;
      break;
    }

    case 'StripePayButtonPaymentAccount': {
      text = translate(payment.Description);

      if (payment.Description === 'Apple_pay') {
        icon = <AppleIcon height={27} />;
      } else {
        icon = <GoogleMicrosoftIcon height={27} />;
      }
      break;
    }

    default:
      text = translate(payment.Description, undefined, {
        missingTranslationMsg: payment.Description
      });
      break;
  }

  return {
    text,
    icon,
    fdKey
  };
}

type RadioOptionProps = {
  payment: WebOrder.State.Payment;
  disabled: boolean;
  translate: ReturnType<typeof getTranslate>;
};
const RadioOption = React.memo(({ payment, disabled, translate }: RadioOptionProps) => {
  const { text, icon, fdKey } = parsePayment({ payment, translate });

  return (
    <FdFormControlLabel
      key={payment.paymentId}
      value={payment.paymentId}
      control={<FdRadio color="primary" disableRipple />}
      data-fd={fdKey}
      disabled={disabled || payment.status !== 'success'}
      label={
        <Grid container alignItems="center" wrap="nowrap">
          <div className="svg-icon">{icon}</div>
          <Typography noWrap component="span" color="textPrimary">
            {text}
          </Typography>
        </Grid>
      }
    />
  );
});

interface PaymentMethodSelectorProps {
  translate: ReturnType<typeof getTranslate>;
  payments: ReturnType<typeof paymentSelectors.getAvailablePayments>;
  selectedPaymentId?: string;
  disabled?: boolean;
}
type Props = PaymentMethodSelectorProps & ReturnType<typeof mapDispatchToProps>;
const PaymentMethodSelector = (props: Props) => {
  const { translate, disabled, payments, selectedPaymentId, selectPayment } = props;

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    selectPayment(event.target.value);
  }, []);

  return (
    <FormControl component="fieldset" fullWidth margin="normal" disabled={disabled}>
      <FormLabel component="legend">
        <Typography variant="h4" component="span" color="textPrimary">
          {translate('Payment_methods')}
        </Typography>
      </FormLabel>

      <RadioGroup
        aria-label={translate('Payment_methods') as string}
        name="payment_method_selection"
        // passing empty string if no init value coz RadioGroup is switching between controlled and uncontrolled state
        value={selectedPaymentId || ''}
        onChange={handleChange}
      >
        {payments.map((payment) => (
          <RadioOption
            key={payment.paymentId}
            payment={payment}
            disabled={disabled}
            translate={translate}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export { PaymentMethodSelector, RadioOption };

const mapDispatchToProps = (dispatch) => {
  return {
    selectPayment: (paymentId: string) => {
      dispatch(basketActions.selectPayment({ paymentId }));
    }
  };
};

export default connect(null, mapDispatchToProps)(PaymentMethodSelector);
